import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getGameFromSlug } from "../../../api/user";
import {
  LeagueTradeProvider,
  isPlausibleSymbol,
  leagueDefaultSymbol,
} from "../../../context/LeagueTradeContext";
import { isEligibleInLeague } from "../../../utils/leagueAssets";
import Char from "../../char";

// /:slug/trade — trading inside a league.
//
// This is the SAME Trade screen the main account uses (char.jsx: chart, toolbar,
// right-hand list, order ticket), wrapped in the league context. Nothing is
// forked: the two consumers that care read the context and adapt —
//
//   • the right-hand list shows the league's assets, not the user's watchlists
//   • the order ticket books every order against this league's game_id
//
// A league is a child of the app, so it gets the app's real trading screen rather
// than a cut-down copy of it.
const LeagueTradePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setResolved(false);
    (async () => {
      try {
        const res = await getGameFromSlug({ slug });
        // A bad slug answers with HTTP 200 + success:false, so read the body.
        const data = res?.data?.success === false ? null : res?.data?.data;
        if (!cancelled) setGame(data?.id ? data : null);
      } catch (e) {
        console.error("League trade lookup failed:", e);
        if (!cancelled) setGame(null);
      } finally {
        if (!cancelled) setResolved(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Start on an asset the league actually allows.
  //
  // `ticker` is shared with every other chart surface, so it can arrive holding
  // something this league can't trade — or, from an older league screen, a league
  // slug, which loads a blank chart. Both cases get replaced before the chart
  // mounts: a restricted league opens on its first asset, an all-markets league
  // on Bitcoin.
  //
  // ONCE per league, on the way in — never again while the screen is open. It
  // used to re-run whenever `game` changed identity, which could overwrite a
  // symbol the user had just picked. The chart would then show what they chose
  // while `ticker` held the league default, and the order ticket reads `ticker`
  // — so a SPY trade could be submitted against X:BTCUSD.
  //
  // DURING RENDER, not in an effect. React runs a child's effects before its
  // parent's, so <Char> was reading `ticker` and drawing its chart before this
  // ever ran: a crypto league opened on SPY, which it cannot trade, and the
  // member's first sight of the league was an asset the Buy button would then
  // refuse. Writing here is a side effect in render, which is normally worth
  // avoiding — but it is idempotent, guarded to once per league by the ref, and
  // it is the only point guaranteed to be before the child reads it.
  const seededSlugRef = useRef(null);
  if (game?.id && seededSlugRef.current !== game.vanity_url) {
    seededSlugRef.current = game.vanity_url;

    // One source of truth for "where does this league's chart open" — see
    // leagueDefaultSymbol. A stocks league used to fall back to Bitcoin here,
    // which it cannot trade.
    const current = localStorage.getItem("ticker");
    // Eligibility, not just membership of the custom list: an "All <category>"
    // league stores no symbols at all, so the old `symbols.includes(current)`
    // test passed anything for it. See utils/leagueAssets.
    const usable = isPlausibleSymbol(current) && isEligibleInLeague(game, current);
    if (!usable) localStorage.setItem("ticker", leagueDefaultSymbol(game));
  }

  useEffect(() => {
    if (resolved && !game) navigate("/contest", { replace: true });
  }, [resolved, game, navigate]);

  if (!resolved || !game) return null;

  return (
    <LeagueTradeProvider game={game}>
      <Char />
    </LeagueTradeProvider>
  );
};

export default LeagueTradePage;
